<template>
  <div class="page-container">
    <div class="content-wrapper">
        <div class="page-header">
            <div class="header-left">
                <h1 class="page-title">{{ page.cardTitle }}</h1>
                <p class="page-subtitle">Configure the point plan details for your system.</p>
            </div>
            <div class="header-actions">
                <button type="button" class="btn-cancel" @click="cancel">Cancel</button>
                <button type="button" class="btn-save" @click="change" :disabled="page.isLoading">
                    <span v-if="page.isLoading"><i class="fas fa-spinner fa-spin me-2"></i> Saving...</span>
                    <span v-else><i class="fas fa-check-circle me-2"></i> Save Plan</span>
                </button>
            </div>
        </div>

        <div v-if="error" class="alert alert-danger mb-4 shadow-sm" role="alert">
            <i class="fas fa-exclamation-triangle me-2"></i> {{ error }}
        </div>

        <div class="main-grid">
            <div class="grid-column main-col">
                <div class="content-card">
                    <div class="card-section">
                        <h3 class="section-title">General Information</h3>
                        <p class="section-desc">Set the name, points, and price for this plan.</p>

                        <div class="form-grid">
                            <!-- Name -->
                            <div class="form-item full">
                                <label>Plan Name <span class="required">*</span></label>
                                <input type="text" v-model="pointPlan.name" class="modern-input" placeholder="e.g. Starter Plan">
                                <div v-if="validation.name" class="error-text">{{ validation.name[0] }}</div>
                            </div>
                            <div class="form-row">
                                <!-- Point -->
                                <div class="form-item half">
                                    <label>Point Amount <span class="required">*</span></label>
                                    <input 
                                        type="text" 
                                        v-model="pointPlan.point" 
                                        class="modern-input" 
                                        @input="handlePointInput"
                                        placeholder="e.g. 1"
                                    >
                                    <div v-if="validation.point" class="error-text">{{ validation.point[0] }}</div>
                                </div>
                                <!-- Price -->
                                <div class="form-item half">
                                    <label>Price (IDR) <span class="required">*</span></label>
                                    <div class="custom-currency-wrapper">
                                        <span class="input-prefix">Rp</span>
                                        <input 
                                            type="text" 
                                            v-model="pointPlan.price" 
                                            class="modern-input with-prefix" 
                                            @input="handlePriceInput"
                                            placeholder="0"
                                        >
                                    </div>
                                    <div v-if="validation.price" class="error-text">{{ validation.price[0] }}</div>
                                </div>
                            </div>

                            <div class="form-item full" v-if="!showCorporatePointPlan && (selectedBusinessProfile.merchant_id == '' || selectedBusinessProfile.merchant_id == null)">
                                <label>Target Merchant</label>
                                <o-select v-model="pointPlan.merchant_id" :options="merchants" placeholder="Select Merchant"></o-select>
                                <div v-if="validation.merchant_id" class="error-text">{{ validation.merchant_id[0] }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="page.isLoading" class="global-loader">
        <div class="spinner-border primary-text" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    </div>
  </div>
</template>

<script>
import { OSelect } from '@/components/OmnihControl';
import { reactive, ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { usePointSystem } from '@/composables/pointsystem'
import store from '@/store';

export default {
    name: 'AddPointPlan',
    components: {
        OSelect
    },
    setup(){
        const { showCorporatePointPlan } = usePointSystem()
        
        const page = reactive({
            isLoading: false,
            cardTitle: 'Create Point Plan'
        });

        const pointPlan = reactive({
            name: '',
            point: '',
            price: '',
            merchant_id: '',
            corporate_id: '',
        });

        const validation = ref([]);
        const error = ref('');
        const router = useRouter();
        const route = useRoute();
        const merchants = ref([]);

        const selectedBusinessProfile = reactive({
            corporate_id: '',
            branch_id: '',
            merchant_id:''
        });

        onMounted(() => {
            loadBusinessProfile();
            if (route.params.id) {
                loadState(route.params.id);
            }
        });

        async function loadState(id) {
            page.isLoading = true;
            try {
                const data = await store.dispatch('Pointplan/read_single', id);
                
                pointPlan.id = data.id;
                pointPlan.name = data.name;
                pointPlan.point = data.point;
                pointPlan.price = priceFormat(data.price);
                
                if (data.corporatePointPlans && data.corporatePointPlans.length > 0) {
                    pointPlan.corporate_id = data.corporatePointPlans[0]?.corporate_id;
                } else if (data.merchant && data.merchant.length > 0) {
                    pointPlan.merchant_id = data.merchant[0]?.id;
                }
                
                page.cardTitle = "Edit Point Plan";
            } catch (error) {
                console.error('Error loading state:', error);
            } finally {
                page.isLoading = false;
            }
        }

        function loadBusinessProfile() {
            store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
                selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
                selectedBusinessProfile.branch_id = businessProfile.branch_id;
                selectedBusinessProfile.merchant_id = businessProfile.merchant_id;
                
                if (showCorporatePointPlan.value) {
                    pointPlan.corporate_id = businessProfile.corporate_id;
                } else {
                    pointPlan.merchant_id = businessProfile.merchant_id;
                }

                if (!showCorporatePointPlan.value && (businessProfile.merchant_id == '' || businessProfile.merchant_id == null)) {
                    loadMerchants();
                }
            });
        }

        function loadMerchants() {
            const filter = {
                branch_id: selectedBusinessProfile.branch_id,
            }

            store.commit('Merchant/add_filter', {filter});
            store.dispatch('Merchant/read').then(() => {
                merchants.value = [...store.state.Merchant.merchants.map((merch)=>({'value':merch.id,'text':merch.name}))]
            })
        }

        function priceFormat(e) {
            if (!e) return '';
            return e.toString()
                .split('.')[0]
                .replace(/[^0-9]/g,'')
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        }

        function handlePriceInput(e) {
            let value = e.target.value;
            // Remove non-digits
            let numericValue = value.replace(/\D/g, '');
            // Remove leading zeros if length > 1
            if (numericValue.length > 1 && numericValue.startsWith('0')) {
                numericValue = numericValue.replace(/^0+/, '');
            }
            // Format IDR
            const formatted = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            pointPlan.price = formatted;
        }

        function handlePointInput(e) {
            let value = e.target.value;
            // Remove non-digits
            value = value.replace(/[^0-9]/g, '');
            // Remove leading zeros if length > 1
            if (value.length > 1 && value.startsWith('0')) {
                value = value.replace(/^0+/, '');
            }
            pointPlan.point = value;
        }

        function change() {
            page.isLoading = true;
            const data = { ...pointPlan };
            data.price = data.price ? data.price.split('.').join("") : '';

            if (showCorporatePointPlan.value) {
                delete data.merchant_id;
            } else {
                delete data.corporate_id;
            }

            const actionType = route.name !== 'PointPlanForm' ? 'Pointplan/update' : 'Pointplan/create';

            store.dispatch(actionType, { data })
                .then(() => {
                    router.push({ name: 'PointPlan' });
                })
                .catch((err) => {
                    if (err.response && err.response.status === 403) {
                        error.value = err.response.data.message;
                    } else if (err.response && err.response.data.error) {
                         try {
                            if (typeof err.response.data.error === 'string') {
                                 validation.value = JSON.parse(err.response.data.error)
                            } else {
                                 validation.value = err.response.data.error
                            }
                        } catch (e) {
                            validation.value = {};
                        }
                    }
                })
                .finally(() => {
                    page.isLoading = false;
                });
        }

        function cancel() {
            router.push({ name: 'PointPlan' });
        }

        return {
            pointPlan,
            validation,
            router,
            change,
            page,
            merchants,
            loadMerchants,
            selectedBusinessProfile,
            cancel,
            error,
            showCorporatePointPlan,
            handlePriceInput,
            handlePointInput
        }
    }
}
</script>

<style scoped>
/* Main Layout */
.page-container {
    width: 100%;
    min-height: calc(100vh - 64px);
    background-color: #f1f4f9;
    padding: 24px 40px;
}

.content-wrapper {
    max-width: 1000px;
    margin: 0 auto;
}

/* Header */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
}

.page-title {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.5px;
}

.page-subtitle {
    color: #64748b;
    font-size: 15px;
    margin: 0;
}

.header-actions {
    display: flex;
    flex-direction: row; 
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
}

/* Buttons */
.btn-cancel {
    padding: 10px 24px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
}

.btn-cancel:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.btn-save {
    padding: 10px 24px;
    background: var(--primary-color, #fc6666); 
    border: none;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 6px -1px var(--primary-subtle, rgba(252, 102, 102, 0.2));
}

.btn-save:hover {
    filter: brightness(0.9);
    box-shadow: 0 10px 15px -3px var(--primary-subtle, rgba(252, 102, 102, 0.3));
}

.btn-save:disabled {
    background: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
}

/* Grid System */
.main-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
}

.content-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 32px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 6px;
}

.section-desc {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 24px;
}

/* Form Styling */
.form-grid {
    display: grid;
    gap: 20px;
}

.form-row {
    display: flex;
    gap: 20px;
}

.form-item {
    margin-bottom: 0;
}

.form-item.full {
    width: 100%;
}

.form-item.half {
    width: 50%;
}

.form-item label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.required {
    color: #ef4444;
}

.modern-input {
    width: 100%;
    padding: 12px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    color: #0f172a;
    transition: all 0.2s;
    background-color: #f8fafc;
}

.modern-input.with-prefix {
    padding-left: 45px;
}

.custom-currency-wrapper {
    position: relative;
    width: 100%;
}

.custom-currency-wrapper .input-prefix {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    font-weight: 500;
    font-size: 14px;
    pointer-events: none;
}

.modern-input:focus {
    border-color: var(--primary-color, #fc6666);
    background-color: #ffffff;
    box-shadow: 0 0 0 3px var(--primary-subtle, rgba(252, 102, 102, 0.1));
    outline: none;
}

.error-text {
    color: #ef4444;
    font-size: 12px;
    margin-top: 6px;
    font-weight: 500;
}

/* Global Loader */
.global-loader {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

/* Responsive */
@media (max-width: 640px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
    .header-actions {
        width: 100%;
        justify-content: flex-start;
    }
    .form-row {
        flex-direction: column;
        gap: 20px;
    }
    .form-item.half {
        width: 100%;
    }
    .page-container {
        padding: 20px;
    }
}
</style>